import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  eventTypes,
  type DepartmentView,
  type EventType,
  type EventView,
  type SeriesGenerationView,
  type StaffOptionView,
  type SupportedLocale,
  type VenueView,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { AudiencePicker } from './AudiencePicker';
import {
  canSubmitEvent,
  emptyEventForm,
  eventRequestBody,
  eventTypeKeys,
  intervalWeekChoices,
  seriesRequestBody,
  toggled,
  weekdayKeys,
  type EventForm as EventFormState,
} from './scheduling';
import { treeOrder } from './VenuesSection';

export interface EventFormProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  departments: readonly DepartmentView[];
  venues: readonly VenueView[];
  defaultDate: string;
  onCreated: (created: { eventId: string | null; series: SeriesGenerationView | null }) => void;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

/**
 * Creates one event or, with "repeat", a series every one to four weeks on chosen weekdays; either
 * starts as a draft unless asked otherwise.
 */
export function EventForm({
  locale,
  base,
  csrfToken,
  departments,
  venues,
  defaultDate,
  onCreated,
}: EventFormProps): React.JSX.Element {
  const [form, setForm] = useState<EventFormState>(() =>
    emptyEventForm(defaultDate, departments[0]?.id ?? ''),
  );
  const [ownMembershipId, setOwnMembershipId] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const update = (changes: Partial<EventFormState>): void => {
    setForm((current) => ({ ...current, ...changes }));
  };

  // Staff are shown without names, so the only choice offered is the person's own membership.
  useEffect(() => {
    if (form.departmentId === '') return;
    let cancelled = false;
    apiRequest<StaffOptionView[]>(
      `${base}/staff-options?departmentId=${encodeURIComponent(form.departmentId)}`,
    ).then(
      (options) => {
        if (!cancelled)
          setOwnMembershipId(options.find((option) => option.isYou)?.organizationUserId ?? null);
      },
      () => {
        if (!cancelled) setOwnMembershipId(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [base, form.departmentId]);

  const resourceOptions = venues
    .filter((venue) => venue.departmentId === null || venue.departmentId === form.departmentId)
    .flatMap((venue) =>
      treeOrder(venue.resources).map(({ resource, depth }) => ({
        id: resource.id,
        label: `${venue.name} › ${'– '.repeat(depth)}${resource.name}`,
      })),
    );

  async function submit(): Promise<void> {
    setErrorKey(null);
    setSubmitting(true);
    try {
      if (form.repeat) {
        const result = await apiRequest<SeriesGenerationView>(`${base}/event-series`, {
          method: 'POST',
          body: seriesRequestBody(form, ownMembershipId),
          csrfToken,
        });
        onCreated({ eventId: null, series: result });
      } else {
        const created = await apiRequest<EventView>(`${base}/events`, {
          method: 'POST',
          body: eventRequestBody(form, ownMembershipId),
          csrfToken,
        });
        onCreated({ eventId: created.id, series: null });
      }
      setForm((current) => ({
        ...emptyEventForm(current.date, current.departmentId),
        eventType: current.eventType,
      }));
      // A fresh picker forgets the names and open rosters of the event just added.
      setPickerKey((current) => current + 1);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      component="form"
      aria-label={translate(locale, 'calendar.create.title')}
      sx={{ mt: 2 }}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Typography variant="h6" component="h3" gutterBottom>
        {translate(locale, 'calendar.create.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            sx={{ minWidth: 180 }}
            label={translate(locale, 'calendar.filter.department')}
            value={form.departmentId}
            onChange={(event) => {
              update({
                departmentId: event.target.value,
                resourceId: '',
                teamIds: [],
                groupIds: [],
              });
            }}
          >
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            sx={{ minWidth: 180 }}
            label={translate(locale, 'calendar.field.type')}
            value={form.eventType}
            onChange={(event) => {
              update({ eventType: event.target.value as EventType });
            }}
          >
            {eventTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {translate(locale, eventTypeKeys[type])}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            required
            sx={{ flexGrow: 1, minWidth: 220 }}
            label={translate(locale, 'calendar.field.title')}
            value={form.title}
            onChange={(event) => {
              update({ title: event.target.value });
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            type="date"
            required
            label={translate(locale, 'calendar.field.date')}
            value={form.date}
            onChange={(event) => {
              update({ date: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="time"
            required
            label={translate(locale, 'calendar.field.startTime')}
            value={form.startTime}
            onChange={(event) => {
              update({ startTime: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="time"
            required
            label={translate(locale, 'calendar.field.endTime')}
            value={form.endTime}
            onChange={(event) => {
              update({ endTime: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            sx={{ minWidth: 260 }}
            label={translate(locale, 'calendar.field.resource')}
            value={form.resourceId}
            onChange={(event) => {
              update({ resourceId: event.target.value });
            }}
          >
            <MenuItem value="">{translate(locale, 'calendar.field.noResource')}</MenuItem>
            {resourceOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            sx={{ minWidth: 220 }}
            label={translate(locale, 'calendar.field.location')}
            value={form.location}
            onChange={(event) => {
              update({ location: event.target.value });
            }}
          />
          <TextField
            size="small"
            sx={{ width: 170 }}
            label={translate(locale, 'calendar.field.capacity')}
            value={form.capacity}
            onChange={(event) => {
              update({ capacity: event.target.value });
            }}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            size="small"
            sx={{ width: 260 }}
            label={translate(locale, 'calendar.field.arrival')}
            value={form.arrivalMinutes}
            onChange={(event) => {
              update({ arrivalMinutes: event.target.value });
            }}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            size="small"
            sx={{ width: 300 }}
            label={translate(locale, 'calendar.field.rsvpHours')}
            value={form.rsvpHours}
            onChange={(event) => {
              update({ rsvpHours: event.target.value });
            }}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
        </Box>

        <AudiencePicker
          key={`${form.departmentId}-${String(pickerKey)}`}
          locale={locale}
          base={base}
          departmentId={form.departmentId}
          selection={form}
          onChange={(selection) => {
            update({
              teamIds: selection.teamIds,
              groupIds: selection.groupIds,
              athleteIds: selection.athleteIds,
            });
          }}
        />

        {ownMembershipId !== null && (
          <FormControlLabel
            label={translate(locale, 'calendar.create.leadSelf')}
            control={
              <Checkbox
                checked={form.leadSelf}
                onChange={(event) => {
                  update({ leadSelf: event.target.checked });
                }}
              />
            }
          />
        )}

        <FormControlLabel
          label={translate(locale, 'calendar.create.repeat')}
          control={
            <Checkbox
              checked={form.repeat}
              onChange={(event) => {
                update({ repeat: event.target.checked });
              }}
            />
          }
        />
        {form.repeat && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {translate(locale, 'calendar.create.weekdays')}
            </Typography>
            <FormGroup row>
              {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
                <FormControlLabel
                  key={weekday}
                  label={translate(locale, weekdayKeys[weekday] ?? 'calendar.weekday.1')}
                  control={
                    <Checkbox
                      checked={form.weekdays.includes(weekday)}
                      onChange={() => {
                        update({ weekdays: toggled(form.weekdays, weekday) });
                      }}
                    />
                  }
                />
              ))}
            </FormGroup>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
              <TextField
                select
                size="small"
                sx={{ minWidth: 200 }}
                label={translate(locale, 'calendar.series.interval')}
                value={form.intervalWeeks}
                onChange={(event) => {
                  update({ intervalWeeks: Number(event.target.value) });
                }}
              >
                {intervalWeekChoices.map((weeks) => (
                  <MenuItem key={weeks} value={weeks}>
                    {weeks === 1
                      ? translate(locale, 'calendar.series.intervalOne')
                      : translate(locale, 'calendar.series.intervalMany').replace(
                          '{weeks}',
                          String(weeks),
                        )}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="date"
                label={translate(locale, 'calendar.create.until')}
                value={form.until}
                onChange={(event) => {
                  update({ until: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <FormControlLabel
                label={translate(locale, 'calendar.create.publishAll')}
                control={
                  <Checkbox
                    checked={form.publishAll}
                    onChange={(event) => {
                      update({ publishAll: event.target.checked });
                    }}
                  />
                }
              />
            </Box>
          </Box>
        )}
        <Box>
          <Button
            type="submit"
            variant="contained"
            sx={{ minHeight: 44 }}
            disabled={!canSubmitEvent(form) || submitting}
          >
            {translate(
              locale,
              form.repeat ? 'calendar.create.submitSeries' : 'calendar.create.submit',
            )}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
